import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Globe, LayoutDashboard, ArrowLeftRight, CreditCard, Send, User, Settings, Bell, LogOut, Menu, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useTheme } from '@/hooks/useTheme';
import { ROUTE_PATHS } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';

const NAV_ITEMS_CONFIG = [
  { path: ROUTE_PATHS.DASHBOARD, icon: LayoutDashboard, labelKey: 'dash_title' as const, exact: true },
  { path: ROUTE_PATHS.DASHBOARD_TRANSACTIONS, icon: ArrowLeftRight, labelKey: 'tx_title' as const },
  { path: ROUTE_PATHS.DASHBOARD_CARDS, icon: CreditCard, labelKey: 'cards_title' as const },
  { path: ROUTE_PATHS.DASHBOARD_TRANSFER, icon: Send, labelKey: 'transfer_title' as const },
  { path: ROUTE_PATHS.DASHBOARD_NOTIFICATIONS, icon: Bell, labelKey: 'notif_title' as const },
  { path: ROUTE_PATHS.DASHBOARD_PROFILE, icon: User, labelKey: 'profile_title' as const },
  { path: ROUTE_PATHS.DASHBOARD_SETTINGS, icon: Settings, labelKey: 'settings_title' as const },
];

interface DashboardLayoutProps {
  children: ReactNode;
  title?: string;
}

export default function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const { user, profile, signOut } = useAuth();
  const { lang, setLang, t } = useI18n();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate(ROUTE_PATHS.LOGIN);
    }
  }, [user, navigate]);

  const initials = profile
    ? `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`.toUpperCase() || 'U'
    : 'U';

  const fullName = profile ? `${profile.first_name} ${profile.last_name}` : t('dash_client');

  useEffect(() => {
    if (!profile?.id) return;

    const fetchUnreadCount = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .eq('is_read', false);
      setUnreadCount(count ?? 0);
    };

    fetchUnreadCount();

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  const handleSignOut = async () => {
    await signOut();
    navigate(ROUTE_PATHS.HOME);
  };

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const getKycBadgeColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'bg-success text-white';
      case 'submitted':
        return 'bg-info text-white';
      case 'rejected':
        return 'bg-danger text-white';
      default:
        return 'bg-warning text-white';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex">
      <aside
        className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col
        transform transition-transform duration-300
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:transform-none
      `}
      >
        <div className="p-6 border-b border-border">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
            <div className="relative">
              <Shield className="w-8 h-8 text-gold" strokeWidth={2} />
              <div className="absolute inset-0 bg-gold/15 blur-lg group-hover:bg-gold/25 transition-all rounded-full" />
            </div>
            <div>
              <p className="font-bold text-foreground leading-tight">CYF24 Bank</p>
              <p className="text-xs text-muted-foreground font-mono tracking-wide">British Bank</p>
            </div>
          </Link>

          <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-muted">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-gold text-navy text-sm font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {fullName}
              </p>
              <p className="text-xs text-muted-foreground truncate">{profile?.email ?? ''}</p>
              {profile?.kyc_status && (
                <Badge className={`mt-1 text-xs px-2 py-0 ${getKycBadgeColor(profile.kyc_status)}`}>
                  {t(`kyc_${profile.kyc_status}`)}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV_ITEMS_CONFIG.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            const showBadge = item.path === ROUTE_PATHS.DASHBOARD_NOTIFICATIONS && unreadCount > 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all relative ${
                  active
                    ? 'bg-gold/15 text-gold border border-gold/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span>{t(item.labelKey)}</span>
                {showBadge && (
                  <Badge className="ml-auto bg-destructive text-destructive-foreground text-xs px-1.5 py-0">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
                {active && !showBadge && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center gap-1 bg-muted rounded-xl px-1 py-1 border border-border">
            <Globe className="w-3 h-3 text-muted-foreground mx-1" />
            <button
              onClick={() => setLang('pl')}
              className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all ${
                lang === 'pl'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              PL
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all ${
                lang === 'en'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              EN
            </button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            className="w-full text-xs"
          >
            {isDark ? '☀️ ' + t('theme_light') : '🌙 ' + t('theme_dark')}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSignOut}
            className="w-full text-xs text-muted-foreground hover:text-destructive"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('sign_out')}
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-card/95 backdrop-blur-xl border-b border-border px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            {title && <h1 className="text-lg font-semibold text-foreground">{title}</h1>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" asChild className="relative">
              <Link to={ROUTE_PATHS.DASHBOARD_NOTIFICATIONS}>
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
                )}
              </Link>
            </Button>
            <Avatar className="w-8 h-8 cursor-pointer">
              <AvatarFallback className="bg-gold text-navy text-xs font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}